export const useFormat = () => {
    /**
     * Extracts numeric value from a currency string
     * @param {string|number} str - Currency string to parse
     * @returns {number} Parsed numeric value or 0 if value is neither a string or a number
     * @example
     * ```js
     * const value = '$1 024.40';
     * console.log(_extractNumber(value)); // 1024.4
     * ```
     */
    const extractNumber = (str) => {
        if (typeof str == "number") {
            return str;
        }
        if (typeof str !== "string") {
            return 0;
        }
        const cleanStr = str
            .replace(/[$€£¥₹]/g, "") // Currency symbol
            .replace(/\s/g, "") // Space
            .replace(/,/g, ".") // Replace coma by dot for decimals
            .replace(/[^\d.-]/g, ""); // Keep only numbers, dot and minus.

        return parseFloat(cleanStr);
    };

    const humanize = (value) => {
        if (value < 1000) return value.toString();

        const units = [
            { value: 1e9, symbol: "B" },
            { value: 1e6, symbol: "M" },
            { value: 1e3, symbol: "k" },
        ];

        for (const unit of units) {
            if (value >= unit.value) {
                const formatted = (value / unit.value).toFixed(1);
                // Remove trailing .0
                return formatted.endsWith(".0")
                    ? formatted.slice(0, -2) + unit.symbol
                    : formatted + unit.symbol;
            }
        }

        return value.toString();
    };

    /**
     * Format a number in a currency value
     * @param {number} amount - the amount to format
     * @param {string} currency - the currency code ('EUR', 'USD', etc.)
     * @param {string} [options:currencyCountry="en-US"] - the locale to use to format the currency
     * @param {number} [options:digit=2] - number of digits for floating part (e.g. 10.20)
     * @param {string} [options:symbol=""] - the currency symbol to "force" in the ouput. If none given, use the default formating.
     * @param {boolean} [options:humanize=false] - convert the value to a more human friendly value (e.g 1.2K)
     * @returns {string} - formatted result
     */
    const formatCurrency = (
        amount,
        currency,
        options = {
            currencyCountry: "en-US",
            digit: 2,
            symbol: "",
            humanize: false,
        }
    ) => {
        const _options = {
            ...{
                currencyCountry: "en-US",
                digit: 2,
                symbol: "",
                humanize: false,
            },
            ...options,
        };
        const currencyFormatter = new Intl.NumberFormat(
            _options.currencyCountry ?? "en-US", // force US for currency symbol.
            {
                style: "currency",
                currency: currency ?? "EUR",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }
        );

        const amountFormatter = new Intl.NumberFormat(
            _options.currencyCountry ?? "en-US",
            {
                style: "decimal",
                minimumFractionDigits: options?.digit ?? 2,
                maximumFractionDigits: options?.digit ?? 2,
            }
        );

        // We format the currency with the 0 value.
        let _template = currencyFormatter.format(0);
        if (_options.symbol) {
            // This regex replace all characters that are not numbers, space, or decimal separators (. ,) with the custom symbol.
            // `+` enforce to replace all by only one symbol and not all matches.
            _template = _template.replace(/[^\d\s.,]+/g, _options.symbol);
        }
        // We format the actual value.
        const _value = _options.humanize
            ? humanize(amount)
            : amountFormatter.format(amount);
        // We inject the value where "0" was.
        return _template.replace("0", _value);
    };

    return { formatCurrency, extractNumber, humanize };
};
